import { action } from '@ember/object';
import { didCancel } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import { waitForLater } from 'ember-simplepractice/utils/waiters';
import Service, { service } from '@ember/service';
import getMessagingApiUrl from '@simplepractice/ember-messaging-client/utils/messaging/messaging-url';

export const EMBER_MESSAGING_ANCHOR_KEY = 'messaging-';
export const EMBER_MESSAGING_CONVERSATION_ID_KEY = 'ember-messaging-conversation-id';

export function processConversationIdFromHash() {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const hash = window.location?.hash;

  if (!hash) {
    return localStorage.removeItem(EMBER_MESSAGING_CONVERSATION_ID_KEY);
  }

  const afterAnchor = hash.split(EMBER_MESSAGING_ANCHOR_KEY)[1];
  const conversationId = afterAnchor ? afterAnchor.split('&')[0] : null;

  if (conversationId) {
    localStorage.setItem(EMBER_MESSAGING_CONVERSATION_ID_KEY, conversationId);
  } else {
    localStorage.removeItem(EMBER_MESSAGING_CONVERSATION_ID_KEY);
  }
}

export default class MessagingWidgetService extends Service {
  @service('spds/toast') toast;
  @service analytics;
  @service messagingInterface;
  @service session;

  @tracked dropdownApi = null;
  isInitialized = false;

  @action
  registerDropdown(api) {
    this.dropdownApi = api;
  }

  @action
  unregisterDropdown() {
    this.dropdownApi = null;
  }

  @action
  closeDropdown() {
    this.dropdownApi?.actions?.close?.();
  }

  openDropdown(conversationId = null) {
    let navigation;
    if (conversationId) {
      navigation = this.goToConversationById(conversationId);
    }

    this.dropdownApi?.actions?.open?.();
    return this.handleNavigationFailure(navigation);
  }

  async handleNavigationFailure(navigation) {
    if (!navigation) return;

    try {
      await navigation;
    } catch (error) {
      if (didCancel(error)) return;

      this.toast.error('Conversation could not be opened. Please try again.', {
        timeout: 5000,
      });
    }
  }

  goToConversationById(conversationId) {
    return this.messagingInterface.goToConversationById(conversationId);
  }

  get unreadConversationCount() {
    return this.messagingInterface.unreadConversationCount;
  }

  get isEmberMessagingLoaded() {
    return this.messagingInterface.startTask.last?.isSuccessful;
  }

  get groupMessagingEnabled() {
    return this.session.currentClientAccess?.client?.permissions?.groupMessaging ?? false;
  }

  async initializeMessaging() {
    if (this.isInitialized) {
      return;
    }

    try {
      const messagingOptions = {
        groupMessagingEnabled: this.groupMessagingEnabled,
        messagingApiUrl: getMessagingApiUrl(),
        trackEvent: this.analytics.track.bind(this.analytics),
      };
      await this.messagingInterface.startTask.perform(messagingOptions);
      this.isInitialized = true;
    } catch (err) {
      window._bugsnagClient?.notify(err);
      this.displayMessagingLoadError();
    }
  }

  async handleMessagingAnchor() {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const conversationId = localStorage.getItem(EMBER_MESSAGING_CONVERSATION_ID_KEY);

    if (conversationId) {
      await waitForLater();

      if (this.dropdownApi) {
        this.openDropdown(conversationId);
      }

      localStorage.setItem(EMBER_MESSAGING_CONVERSATION_ID_KEY, '');
    }
  }

  handleMessagingAnchorOnLoad = modifier(() => {
    this.handleMessagingAnchor();
  });

  displayMessagingLoadError() {
    this.toast.error('Messaging service could not start.', {
      timeout: 10000,
      cta: {
        text: 'Retry',
        action: async () => {
          this.isInitialized = false;
          await this.initializeMessaging();
        },
      },
    });
  }

  messagingCleanup() {
    this.messagingInterface.stop();
    this.isInitialized = false;
  }
}
